import { useEffect, useMemo, useRef, useState } from 'react';
import type { RoundSnapshot } from '@hypevault/chaos-scramble';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { gameSounds } from '../lib/gameSounds';

/**
 * Chaos Scramble — full-screen AAA HUD overlay (1920×1080 native).
 *
 * Layout regions:
 *   - Top bar: theme, round, BIG arc-progress timer, words-found counter
 *   - Center: huge hex letter tiles in a single row (or two rows if needed)
 *   - Bottom: scrolling found-words feed (or right rail, configurable)
 *   - Right rail: streak banner slot
 *   - Boss mode: red/gold palette + vignette pulse + giant title card +
 *     confetti winner takeover
 */

interface Fields {
  /** Where the found-words feed sits. 'bottom' = horizontal strip across
   * the bottom; 'right' = vertical column on the right rail. */
  feedSide?: 'bottom' | 'right';
  /** Overall overlay opacity 0..1. */
  opacity?: number;
  /** Play synthesized fanfare at boss-round start and victory chord on boss
   * solve. Streamers who use their own SFX from a separate source can flip
   * this off. Default true. */
  bossAudio?: boolean;
  /** Boss audio cue gain (0..1). Default 0.6. */
  bossAudioVolume?: number;
}

interface Props {
  streamerId: string;
  snapshot: RoundSnapshot | null;
  fields: Fields;
}

const PALETTE = {
  bg: '#0a0a0f',
  purple: '#9333ea',
  cyan: '#06b6d4',
  magenta: '#e879f9',
  gold: '#f59e0b',
  red: '#ef4444',
};

export function ChaosScrambleWidget({ streamerId, snapshot, fields }: Props) {
  const initial = useRef<RoundSnapshot | null>(snapshot);
  const [latest, setLatest] = useState<RoundSnapshot | null>(snapshot);
  const prevRef = useRef<RoundSnapshot | null>(snapshot);
  useEffect(() => {
    if (initial.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    const ac = new AbortController();
    fetch(`/api/chaos-scramble/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (json: { snapshot?: RoundSnapshot; sound?: typeof DEFAULT_GAME_SOUND_CONFIG } | null) => {
          if (json?.snapshot) setLatest(json.snapshot);
          gameSounds.configure(json?.sound ?? DEFAULT_GAME_SOUND_CONFIG);
        },
      )
      .catch(() => undefined);
    return () => ac.abort();
  }, []);
  useEffect(() => {
    if (snapshot) setLatest(snapshot);
  }, [snapshot]);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = latest;
    if (!latest) return;
    if (!prev || prev.roundId !== latest.roundId) {
      void gameSounds.play(latest.isBossRound ? 'suspense_build' : 'game_start');
      return;
    }
    if (prev.found.length < latest.found.length) {
      void gameSounds.play('correct');
    }
    if (prev.streakBanner?.setAt !== latest.streakBanner?.setAt && latest.streakBanner) {
      void gameSounds.play('celebration');
    }
    if (prev.scrambledLetters !== latest.scrambledLetters) {
      void gameSounds.play('phase_change');
    }
  }, [latest]);
  void streamerId;

  const opacity = typeof fields.opacity === 'number' ? fields.opacity : 1;
  const feedSide = fields.feedSide ?? 'bottom';
  const bossAudio = fields.bossAudio !== false;
  const bossAudioVolume =
    typeof fields.bossAudioVolume === 'number'
      ? Math.max(0, Math.min(1, fields.bossAudioVolume))
      : 0.6;

  useBossAudioCues(latest, bossAudio, bossAudioVolume);

  if (!latest) {
    return <IdleStage opacity={opacity} />;
  }
  return <ChaosScrambleStage snapshot={latest} opacity={opacity} feedSide={feedSide} />;
}

// ──────────────────────────────────────────────────────────────────────────
// Audio cues — synthesized via Web Audio so no asset has to ship/load
// ──────────────────────────────────────────────────────────────────────────

let audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioCtx) return audioCtx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  audioCtx = new Ctor();
  return audioCtx;
}

/** Brass-fanfare-ish ascending arpeggio. */
function playBossStartCue(volume: number) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  void ctx.resume();
  const notes = [261.63, 329.63, 392.0, 523.25]; // C4 E4 G4 C5
  notes.forEach((freq, i) => {
    const t0 = ctx.currentTime + i * 0.12;
    const dur = 0.45;
    const o = ctx.createOscillator();
    const o2 = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sawtooth';
    o.frequency.value = freq;
    o2.type = 'square';
    o2.frequency.value = freq * 2;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.4 * volume, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g);
    o2.connect(g);
    g.connect(ctx.destination);
    o.start(t0);
    o2.start(t0);
    o.stop(t0 + dur);
    o2.stop(t0 + dur);
  });
}

/** Big triumphant chord stab + descending sparkle. */
function playBossSolvedCue(volume: number) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  void ctx.resume();
  // C major chord, fortissimo
  const chord = [261.63, 329.63, 392.0, 523.25, 659.25];
  const t0 = ctx.currentTime;
  chord.forEach((freq) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.3 * volume, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 1.2);
    o.connect(g).connect(ctx.destination);
    o.start(t0);
    o.stop(t0 + 1.2);
  });
  // Sparkle: 5 quick high notes descending
  const sparkle = [1568.0, 1318.51, 1046.5, 880.0, 783.99];
  sparkle.forEach((freq, i) => {
    const ts = t0 + 0.05 + i * 0.07;
    const dur = 0.18;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, ts);
    g.gain.linearRampToValueAtTime(0.18 * volume, ts + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, ts + dur);
    o.connect(g).connect(ctx.destination);
    o.start(ts);
    o.stop(ts + dur);
  });
}

function useBossAudioCues(snapshot: RoundSnapshot | null, enabled: boolean, volume: number) {
  const prevIsBoss = useRef<boolean>(false);
  const prevRoundId = useRef<string | null>(null);
  const playedSolve = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !snapshot) return;

    // Boss-start: transition into a boss round (either toggle on, or new
    // round id that is itself a boss). Guard against the initial mount when
    // we connect mid-round — only fire on transitions.
    const isBossNow = snapshot.isBossRound;
    const roundChanged = prevRoundId.current !== null && prevRoundId.current !== snapshot.roundId;
    const startedBoss =
      prevRoundId.current !== null && isBossNow && (!prevIsBoss.current || roundChanged);
    if (startedBoss) playBossStartCue(volume);

    // Boss-solved: a found word lands during a boss round. The 'found' array
    // length goes from 0 → 1 on solve. We key by roundId so repeat snapshots
    // don't re-fire.
    if (isBossNow && snapshot.found.length > 0 && !playedSolve.current.has(snapshot.roundId)) {
      playedSolve.current.add(snapshot.roundId);
      playBossSolvedCue(volume);
    }

    prevIsBoss.current = isBossNow;
    prevRoundId.current = snapshot.roundId;
  }, [snapshot, enabled, volume]);
}

function IdleStage({ opacity }: { opacity: number }) {
  return (
    <div className="cs-stage cs-idle" style={{ opacity }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Rajdhani:wght@500;600;700&display=swap"
        rel="stylesheet"
      />
      <div className="cs-idle-block">
        <div className="cs-idle-eyebrow">Chaos Scramble</div>
        <div className="cs-idle-title">WAITING FOR ROUND</div>
        <div className="cs-idle-dot" />
      </div>
      <ChaosScrambleStyles />
    </div>
  );
}

function ChaosScrambleStage({
  snapshot,
  opacity,
  feedSide,
}: {
  snapshot: RoundSnapshot;
  opacity: number;
  feedSide: 'bottom' | 'right';
}) {
  // ── Clocks ─────────────────────────────────────────────────────────────
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);
  const remaining = Math.max(0, snapshot.endsAt - now);
  const total = snapshot.isBossRound ? 180_000 : 90_000;
  const progress = Math.min(1, remaining / total);
  const remainingSec = Math.ceil(remaining / 1000);
  const dangerTime = remaining < 15_000;
  const lowestSec = Math.ceil((snapshot.nextScrambleAt - now) / 1000);
  const scrambleWarning = !snapshot.isBossRound && lowestSec <= 3 && lowestSec >= 1;

  // ── Scramble animation trigger ─────────────────────────────────────────
  const [scrambleNonce, setScrambleNonce] = useState(0);
  const prevScrambled = useRef(snapshot.scrambledLetters);
  useEffect(() => {
    if (snapshot.scrambledLetters !== prevScrambled.current) {
      prevScrambled.current = snapshot.scrambledLetters;
      setScrambleNonce((n) => n + 1);
    }
  }, [snapshot.scrambledLetters]);

  // ── Streak banner ──────────────────────────────────────────────────────
  const streakBanner = snapshot.streakBanner;
  const showBanner = streakBanner !== null && now - streakBanner.setAt < 4_000;

  // ── Letters ────────────────────────────────────────────────────────────
  const letters = snapshot.scrambledLetters.split('');

  // ── Boss mode ──────────────────────────────────────────────────────────
  const isBoss = snapshot.isBossRound;
  const accent = isBoss ? PALETTE.gold : PALETTE.purple;
  const accent2 = isBoss ? PALETTE.red : PALETTE.cyan;
  const cssVars: React.CSSProperties = {
    ['--cs-accent' as string]: accent,
    ['--cs-accent2' as string]: accent2,
    ['--cs-magenta' as string]: PALETTE.magenta,
    ['--cs-gold' as string]: PALETTE.gold,
    ['--cs-red' as string]: PALETTE.red,
    opacity,
  };

  return (
    <div className={`cs-stage ${isBoss ? 'cs-boss' : ''}`} data-feed={feedSide} style={cssVars}>
      <link
        href="https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Rajdhani:wght@500;600;700&display=swap"
        rel="stylesheet"
      />
      <ChaosScrambleStyles />

      {/* Background atmospherics: subtle scanlines, grid mesh, corner glow */}
      <div className="cs-bg" aria-hidden>
        <div className="cs-bg-grid" />
        <div className="cs-bg-scan" />
        <div className="cs-bg-glow cs-bg-glow-tl" />
        <div className="cs-bg-glow cs-bg-glow-br" />
      </div>

      {isBoss && <div className="cs-vignette" aria-hidden />}

      {/* ─── TOP BAR ─── */}
      <header className="cs-top">
        <div className="cs-top-left">
          <div className="cs-brand">
            <span className="cs-brand-mark" />
            <span className="cs-brand-text">CHAOS SCRAMBLE</span>
          </div>
          <div className="cs-theme">
            <span className="cs-theme-emoji">{themeEmoji(snapshot.theme)}</span>
            <span className="cs-theme-name">{snapshot.theme}</span>
          </div>
          <div className="cs-meta">
            <span className="cs-meta-pill">
              <span className="cs-meta-label">Round</span>
              <span className="cs-meta-value">{snapshot.roundNumber}</span>
            </span>
            <BossRoundChip roundNumber={snapshot.roundNumber} isBossRound={snapshot.isBossRound} />
          </div>
        </div>

        <div className="cs-top-timer">
          <ArcTimer progress={progress} danger={dangerTime} isBoss={isBoss} />
          <div className={`cs-timer-digits ${dangerTime ? 'cs-danger' : ''}`}>
            {formatTime(remainingSec)}
          </div>
          <div className="cs-timer-label">{isBoss ? 'BOSS TIME' : 'TIME LEFT'}</div>
        </div>

        <div className="cs-top-right">
          <div className="cs-stat-block">
            <div className="cs-stat-eyebrow">Words found</div>
            <div className="cs-stat-line">
              <span className="cs-stat-num">{snapshot.found.length}</span>
              <span className="cs-stat-slash">/</span>
              <span className="cs-stat-total">{snapshot.totalWords}</span>
            </div>
          </div>
          <div className="cs-stat-block">
            <div className="cs-stat-eyebrow">Round gems</div>
            <div className="cs-stat-line">
              <span className="cs-stat-num cs-stat-gems">{snapshot.gemsAwardedThisRound}</span>
              <span className="cs-stat-gem-icon">💎</span>
            </div>
          </div>
        </div>
      </header>

      {/* Boss-mode title slam */}
      {isBoss && <BossTitleSlam />}

      {/* ─── CENTER LETTER POOL ─── */}
      <main className="cs-pool-wrap">
        <div className="cs-pool" key={scrambleNonce}>
          {letters.map((ch, i) => (
            <LetterTile
              key={`${scrambleNonce}-${i}`}
              letter={ch}
              index={i}
              total={letters.length}
              isBoss={isBoss}
              scrambleWarning={scrambleWarning}
            />
          ))}
        </div>
        <div className="cs-hint">
          {isBoss
            ? 'Type the BOSS WORD in chat — 9 to 12 letters'
            : 'Type any word in chat using these letters'}
        </div>
      </main>

      {/* ─── STREAK BANNER (right rail) ─── */}
      {showBanner && streakBanner && (
        <StreakBanner viewer={streakBanner.viewerName} multiplier={streakBanner.multiplier} />
      )}

      {/* ─── FOUND WORDS FEED ─── */}
      <FoundFeed snapshot={snapshot} side={feedSide} />

      {/* ─── BOSS WINNER TAKEOVER ─── */}
      {isBoss && snapshot.found.length >= 1 && <BossWinner found={snapshot.found[0]!} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Pieces
// ──────────────────────────────────────────────────────────────────────────

function ArcTimer({
  progress,
  danger,
  isBoss,
}: {
  progress: number;
  danger: boolean;
  isBoss: boolean;
}) {
  const r = 72;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - progress);
  const color = danger ? PALETTE.red : isBoss ? PALETTE.gold : PALETTE.cyan;
  return (
    <svg className="cs-arc" viewBox="0 0 180 180" aria-hidden>
      <defs>
        <linearGradient id="cs-arc-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop
            offset="100%"
            stopColor={danger ? '#fb923c' : isBoss ? PALETTE.red : PALETTE.purple}
          />
        </linearGradient>
      </defs>
      <circle cx="90" cy="90" r={r} stroke="rgba(255,255,255,0.07)" strokeWidth="6" fill="none" />
      <circle
        cx="90"
        cy="90"
        r={r}
        stroke="url(#cs-arc-grad)"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 90 90)"
        style={{
          transition: 'stroke-dashoffset 0.2s linear',
          filter: `drop-shadow(0 0 12px ${color})`,
        }}
      />
    </svg>
  );
}

function LetterTile({
  letter,
  index,
  total,
  isBoss,
  scrambleWarning,
}: {
  letter: string;
  index: number;
  total: number;
  isBoss: boolean;
  scrambleWarning: boolean;
}) {
  const angle = ((index * 137) % 360) * (Math.PI / 180);
  const dist = 320 + ((index * 53) % 200);
  const rotate = ((index * 89) % 80) - 40;
  const dx = Math.cos(angle) * dist;
  const dy = Math.sin(angle) * dist;
  const delay = (index * 40) % 400;
  return (
    <div
      className={`cs-tile ${isBoss ? 'cs-tile-boss' : ''} ${scrambleWarning ? 'cs-tile-warn' : ''}`}
      style={
        {
          ['--cs-dx' as string]: `${dx}px`,
          ['--cs-dy' as string]: `${dy}px`,
          ['--cs-rot' as string]: `${rotate}deg`,
          animationDelay: `${delay}ms`,
        } as React.CSSProperties
      }
      data-index={index}
      data-total={total}
    >
      <span className="cs-tile-shockwave" aria-hidden />
      <span className="cs-tile-glyph">{letter}</span>
    </div>
  );
}

function StreakBanner({ viewer, multiplier }: { viewer: string; multiplier: number }) {
  return (
    <div className="cs-streak-banner" key={`${viewer}-${multiplier}`}>
      <div className="cs-streak-flames" aria-hidden>
        {Array.from({ length: 16 }).map((_, i) => (
          <span key={i} className="cs-streak-flame" style={{ animationDelay: `${i * 70}ms` }} />
        ))}
      </div>
      <div className="cs-streak-text">
        <span className="cs-streak-fire">🔥</span>
        <div className="cs-streak-name-block">
          <div className="cs-streak-eyebrow">ON FIRE</div>
          <div className="cs-streak-name">{viewer}</div>
        </div>
        <div className="cs-streak-mult">{multiplier.toFixed(multiplier % 1 ? 1 : 0)}×</div>
      </div>
    </div>
  );
}

function FoundFeed({ snapshot, side }: { snapshot: RoundSnapshot; side: 'bottom' | 'right' }) {
  const recent = useMemo(() => snapshot.found.slice(-8).reverse(), [snapshot.found]);
  if (recent.length === 0) {
    return (
      <div className={`cs-feed cs-feed-${side} cs-feed-empty`}>
        <span>Type a word in chat using these letters to score.</span>
      </div>
    );
  }
  return (
    <div className={`cs-feed cs-feed-${side}`}>
      {recent.map((f, i) => (
        <div
          key={`${f.word}-${f.viewerName}-${f.foundAt}`}
          className={`cs-feed-row ${f.isThematic ? 'cs-feed-thematic' : ''} ${
            snapshot.isBossRound ? 'cs-feed-boss' : ''
          }`}
          style={{ opacity: 1 - i * 0.1 }}
        >
          <span className="cs-feed-check">✓</span>
          <span className="cs-feed-word">{f.word}</span>
          <span className="cs-feed-by">{f.viewerName}</span>
          <span className="cs-feed-gems">+{f.gemsAwarded} 💎</span>
        </div>
      ))}
    </div>
  );
}

function BossTitleSlam() {
  return (
    <div className="cs-boss-title" aria-hidden>
      <span className="cs-boss-title-flash" />
      <span className="cs-boss-title-text">⚡ BOSS ROUND ⚡</span>
    </div>
  );
}

function BossRoundChip({
  roundNumber,
  isBossRound,
}: {
  roundNumber: number;
  isBossRound: boolean;
}) {
  if (isBossRound) {
    return <span className="cs-meta-pill cs-meta-pill-boss">BOSS ACTIVE</span>;
  }
  const next = 5 - (roundNumber % 5);
  return (
    <span className="cs-meta-pill cs-meta-pill-next-boss">
      <span className="cs-meta-label">Next boss</span>
      <span className="cs-meta-value">{next}</span>
    </span>
  );
}

function BossWinner({
  found,
}: {
  found: { word: string; viewerName: string; gemsAwarded: number };
}) {
  return (
    <div className="cs-boss-winner" data-audio-cue="boss-solved">
      <div className="cs-boss-winner-confetti" aria-hidden>
        {Array.from({ length: 60 }).map((_, i) => (
          <span
            key={i}
            className="cs-confetti-piece"
            style={{
              left: `${(i * 53) % 100}%`,
              backgroundColor: i % 2 === 0 ? PALETTE.gold : PALETTE.magenta,
              animationDelay: `${(i * 60) % 1500}ms`,
            }}
          />
        ))}
      </div>
      <div className="cs-boss-winner-text">
        <div className="cs-boss-winner-label">BOSS SOLVED</div>
        <div className="cs-boss-winner-word">{found.word}</div>
        <div className="cs-boss-winner-by">{found.viewerName}</div>
        <div className="cs-boss-winner-prize">+{found.gemsAwarded} 💎</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Utility
// ──────────────────────────────────────────────────────────────────────────

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function themeEmoji(theme: string): string {
  const t = theme.toLowerCase();
  if (/game|gam/.test(t)) return '🎮';
  if (/movie|film/.test(t)) return '🎬';
  if (/food|drink|cook/.test(t)) return '🍔';
  if (/anim/.test(t)) return '🐾';
  if (/sport/.test(t)) return '⚽';
  if (/music|song/.test(t)) return '🎵';
  if (/space|sci/.test(t)) return '🚀';
  if (/fantasy|magic/.test(t)) return '🐉';
  if (/horror|scary/.test(t)) return '💀';
  if (/stream|twitch/.test(t)) return '🎙️';
  if (/meme/.test(t)) return '😂';
  if (/myth/.test(t)) return '🏛️';
  if (/travel|place|country|city/.test(t)) return '✈️';
  if (/tech|internet|web/.test(t)) return '💻';
  if (/nature|forest|ocean/.test(t)) return '🌿';
  return '✨';
}

// ──────────────────────────────────────────────────────────────────────────
// Styles — designed for 1920×1080 canvas, scales via vw/vh
// ──────────────────────────────────────────────────────────────────────────

function ChaosScrambleStyles() {
  return (
    <style>{`
      .cs-stage {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        font-family: 'Rajdhani', system-ui, sans-serif;
        color: #fff;
        overflow: hidden;
        background:
          radial-gradient(ellipse at 20% 0%, rgba(147,51,234,0.18), transparent 55%),
          radial-gradient(ellipse at 80% 100%, rgba(6,182,212,0.16), transparent 55%),
          linear-gradient(180deg, #0a0814 0%, #050208 100%);
        pointer-events: none;
      }
      .cs-stage.cs-boss {
        background:
          radial-gradient(ellipse at 50% 0%, rgba(239,68,68,0.22), transparent 55%),
          radial-gradient(ellipse at 50% 100%, rgba(245,158,11,0.18), transparent 55%),
          linear-gradient(180deg, #140503 0%, #0a0202 100%);
      }

      /* ── Background atmospherics ───────────────────────────────────── */
      .cs-bg {
        position: absolute;
        inset: 0;
        pointer-events: none;
      }
      .cs-bg-grid {
        position: absolute;
        inset: 0;
        background-image:
          linear-gradient(rgba(147,51,234,0.06) 1px, transparent 1px),
          linear-gradient(90deg, rgba(6,182,212,0.06) 1px, transparent 1px);
        background-size: 80px 80px;
        mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
      }
      .cs-bg-scan {
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
          0deg,
          transparent 0px,
          transparent 3px,
          rgba(255,255,255,0.012) 3px,
          rgba(255,255,255,0.012) 4px
        );
      }
      .cs-bg-glow {
        position: absolute;
        width: 600px;
        height: 600px;
        border-radius: 50%;
        filter: blur(120px);
        opacity: 0.5;
      }
      .cs-bg-glow-tl {
        top: -200px;
        left: -200px;
        background: radial-gradient(circle, var(--cs-accent), transparent 65%);
      }
      .cs-bg-glow-br {
        bottom: -200px;
        right: -200px;
        background: radial-gradient(circle, var(--cs-accent2), transparent 65%);
      }

      .cs-vignette {
        position: absolute;
        inset: 0;
        pointer-events: none;
        box-shadow: 0 0 400px 160px rgba(239,68,68,0.25) inset;
        animation: cs-vignette-pulse 1.6s ease-in-out infinite;
      }
      @keyframes cs-vignette-pulse {
        0%, 100% { opacity: 0.5; }
        50%      { opacity: 1; }
      }

      /* ── Top bar ───────────────────────────────────────────────────── */
      .cs-top {
        position: relative;
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        gap: 32px;
        align-items: center;
        padding: 32px 56px 24px;
        z-index: 2;
        animation: cs-top-in 0.7s cubic-bezier(0.2,0.9,0.3,1.2) both;
      }
      @keyframes cs-top-in {
        from { transform: translateY(-30px); opacity: 0; }
        to   { transform: translateY(0); opacity: 1; }
      }
      .cs-top::after {
        content: '';
        position: absolute;
        left: 56px;
        right: 56px;
        bottom: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, var(--cs-accent), var(--cs-accent2), transparent);
        opacity: 0.7;
      }
      .cs-top-left {
        display: flex;
        align-items: center;
        gap: 28px;
      }
      .cs-brand {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .cs-brand-mark {
        width: 14px;
        height: 36px;
        background: linear-gradient(180deg, var(--cs-accent), var(--cs-accent2));
        box-shadow: 0 0 16px var(--cs-accent), 0 0 32px var(--cs-accent2);
        clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
        animation: cs-brand-pulse 2.4s ease-in-out infinite;
      }
      @keyframes cs-brand-pulse {
        0%, 100% { transform: scaleY(1); }
        50%      { transform: scaleY(1.15); }
      }
      .cs-brand-text {
        font-family: 'Orbitron', sans-serif;
        font-size: 14px;
        font-weight: 900;
        letter-spacing: 6px;
        color: rgba(255,255,255,0.7);
      }
      .cs-theme {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 10px 20px;
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.07);
        border-radius: 16px;
        backdrop-filter: blur(8px);
      }
      .cs-theme-emoji { font-size: 32px; line-height: 1; }
      .cs-theme-name {
        font-family: 'Orbitron', sans-serif;
        font-size: 24px;
        font-weight: 900;
        letter-spacing: 1px;
        text-shadow: 0 0 18px var(--cs-accent);
      }
      .cs-meta {
        display: flex;
        gap: 8px;
      }
      .cs-meta-pill {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 14px;
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.1);
        border-radius: 999px;
        font-family: 'Orbitron', sans-serif;
      }
      .cs-meta-label {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 3px;
        text-transform: uppercase;
        color: rgba(255,255,255,0.55);
      }
      .cs-meta-value {
        font-size: 18px;
        font-weight: 900;
        color: var(--cs-accent2);
        text-shadow: 0 0 10px var(--cs-accent2);
      }
      .cs-meta-pill-next-boss .cs-meta-value {
        color: ${PALETTE.gold};
        text-shadow: 0 0 12px ${PALETTE.gold};
      }
      .cs-meta-pill-boss {
        background: rgba(245,158,11,0.16);
        border-color: rgba(245,158,11,0.6);
        color: ${PALETTE.gold};
        font-size: 12px;
        font-weight: 800;
        letter-spacing: 2px;
        text-shadow: 0 0 12px ${PALETTE.gold};
        animation: cs-pill-pulse 1.4s ease-in-out infinite;
      }
      @keyframes cs-pill-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.5); }
        50%      { box-shadow: 0 0 0 12px rgba(245,158,11,0); }
      }

      /* Center mega-timer */
      .cs-top-timer {
        position: relative;
        width: 180px;
        height: 180px;
        display: grid;
        place-items: center;
      }
      .cs-arc {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
      }
      .cs-timer-digits {
        font-family: 'Orbitron', monospace;
        font-size: 48px;
        font-weight: 900;
        letter-spacing: 2px;
        z-index: 1;
        text-shadow: 0 0 24px var(--cs-accent2);
        line-height: 1;
      }
      .cs-timer-label {
        position: absolute;
        bottom: 28px;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 4px;
        color: rgba(255,255,255,0.55);
      }
      .cs-timer-digits.cs-danger {
        color: ${PALETTE.red};
        text-shadow: 0 0 24px ${PALETTE.red};
        animation: cs-danger-pulse 0.6s ease-in-out infinite;
      }
      @keyframes cs-danger-pulse {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.1); }
      }

      /* Right stats */
      .cs-top-right {
        display: flex;
        justify-content: flex-end;
        gap: 24px;
      }
      .cs-stat-block {
        text-align: right;
        padding: 8px 16px;
        background: rgba(255,255,255,0.02);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 14px;
        backdrop-filter: blur(8px);
      }
      .cs-stat-eyebrow {
        font-family: 'Orbitron', sans-serif;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 3px;
        text-transform: uppercase;
        color: rgba(255,255,255,0.5);
      }
      .cs-stat-line {
        display: inline-flex;
        align-items: baseline;
        gap: 4px;
        font-family: 'Orbitron', monospace;
        font-weight: 900;
        margin-top: 2px;
      }
      .cs-stat-num {
        font-size: 36px;
        color: var(--cs-accent2);
        text-shadow: 0 0 18px var(--cs-accent2);
      }
      .cs-stat-gems {
        color: ${PALETTE.gold};
        text-shadow: 0 0 18px ${PALETTE.gold};
      }
      .cs-stat-slash {
        font-size: 24px;
        color: rgba(255,255,255,0.3);
      }
      .cs-stat-total {
        font-size: 24px;
        color: rgba(255,255,255,0.7);
      }
      .cs-stat-gem-icon { font-size: 28px; }

      /* ── Boss title slam ─────────────────────────────────────────── */
      .cs-boss-title {
        position: relative;
        text-align: center;
        font-family: 'Orbitron', sans-serif;
        font-weight: 900;
        font-size: 64px;
        letter-spacing: 12px;
        color: ${PALETTE.gold};
        margin: 24px 56px 0;
        padding: 16px 0;
        text-shadow:
          0 0 32px ${PALETTE.gold},
          0 0 64px ${PALETTE.red};
        animation: cs-boss-title-in 0.8s cubic-bezier(0.2,0.9,0.3,1.5) both;
        z-index: 3;
      }
      .cs-boss-title-flash {
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent, rgba(245,158,11,0.5), transparent);
        animation: cs-boss-sweep 2s ease-in-out infinite;
        pointer-events: none;
      }
      @keyframes cs-boss-title-in {
        0%   { transform: translateY(-80px) scaleX(1.4); opacity: 0; letter-spacing: 32px; }
        100% { transform: translateY(0) scaleX(1); opacity: 1; letter-spacing: 12px; }
      }
      @keyframes cs-boss-sweep {
        0%   { transform: translateX(-100%); opacity: 0; }
        50%  { opacity: 1; }
        100% { transform: translateX(100%); opacity: 0; }
      }

      /* ── Letter pool (center stage) ───────────────────────────────── */
      .cs-pool-wrap {
        position: relative;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin-top: 40px;
        margin-bottom: 40px;
        z-index: 2;
        height: calc(100% - 280px);
      }
      .cs-pool {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 20px;
        padding: 0 56px;
        max-width: 1700px;
      }
      .cs-tile {
        position: relative;
        width: 140px;
        height: 158px;
        clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
        background:
          radial-gradient(circle at 30% 25%, rgba(255,255,255,0.22), transparent 60%),
          linear-gradient(160deg, rgba(40,32,72,0.95), rgba(15,10,30,0.95));
        display: grid;
        place-items: center;
        animation: cs-tile-slam 1.0s cubic-bezier(0.18,0.9,0.2,1.4) both;
      }
      .cs-tile::after {
        content: '';
        position: absolute;
        inset: 2px;
        clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
        background: transparent;
        box-shadow: 0 0 40px 0 var(--cs-accent2) inset;
        pointer-events: none;
      }
      .cs-tile-shockwave {
        position: absolute;
        inset: 0;
        clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
        background: radial-gradient(circle at 50% 50%, var(--cs-accent2), transparent 60%);
        opacity: 0;
        animation: cs-shockwave 1.0s ease-out both;
        animation-delay: inherit;
        pointer-events: none;
      }
      @keyframes cs-shockwave {
        0%   { opacity: 0; transform: scale(0.4); }
        30%  { opacity: 0.7; transform: scale(1.1); }
        100% { opacity: 0; transform: scale(1.5); }
      }
      .cs-tile-glyph {
        font-family: 'Orbitron', monospace;
        font-weight: 900;
        font-size: 84px;
        color: #fff;
        text-shadow:
          0 0 16px var(--cs-accent),
          0 0 32px var(--cs-accent2);
        animation: cs-glyph-snap 1.0s ease-out both;
        animation-delay: inherit;
        line-height: 1;
      }
      @keyframes cs-tile-slam {
        0% {
          transform: translate(var(--cs-dx, 0px), var(--cs-dy, 0px)) rotate(var(--cs-rot, 0deg)) scale(0.5);
          opacity: 0;
          filter: blur(10px);
        }
        45% {
          opacity: 1;
          filter: blur(0);
        }
        65% {
          transform: translate(0,0) rotate(0) scale(1.12);
        }
        80% {
          transform: translate(0,0) rotate(0) scale(0.94);
        }
        100% {
          transform: translate(0,0) rotate(0) scale(1);
        }
      }
      @keyframes cs-glyph-snap {
        0%   { opacity: 0; transform: scale(0.4); }
        45%  { opacity: 0; }
        65%  { opacity: 1; transform: scale(1.2); }
        100% { opacity: 1; transform: scale(1); }
      }
      .cs-tile-warn {
        animation: cs-tile-slam 1.0s cubic-bezier(0.18,0.9,0.2,1.4) both, cs-tile-warn 0.5s ease-in-out infinite alternate 1s;
      }
      @keyframes cs-tile-warn {
        0%   { box-shadow: 0 0 40px 0 var(--cs-accent2) inset; }
        100% { box-shadow: 0 0 60px 8px ${PALETTE.red} inset; }
      }
      .cs-tile-boss {
        width: 160px;
        height: 180px;
        background:
          radial-gradient(circle at 30% 25%, rgba(255,255,255,0.28), transparent 60%),
          linear-gradient(160deg, rgba(72,48,14,0.95), rgba(34,12,8,0.97));
      }
      .cs-tile-boss::after {
        box-shadow: 0 0 48px 0 ${PALETTE.gold} inset;
      }
      .cs-tile-boss .cs-tile-glyph {
        font-size: 96px;
        color: ${PALETTE.gold};
        text-shadow:
          0 0 18px ${PALETTE.gold},
          0 0 36px ${PALETTE.red};
      }

      .cs-hint {
        margin-top: 32px;
        font-family: 'Rajdhani', sans-serif;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: 3px;
        text-transform: uppercase;
        color: rgba(255,255,255,0.45);
      }

      /* ── Streak banner ─────────────────────────────────────────────── */
      .cs-streak-banner {
        position: absolute;
        top: 240px;
        right: 0;
        display: flex;
        align-items: center;
        padding: 18px 36px 18px 28px;
        background: linear-gradient(90deg, rgba(232,121,249,0.22), rgba(232,121,249,0.55));
        border: 1px solid rgba(232,121,249,0.7);
        border-radius: 999px 0 0 999px;
        box-shadow:
          0 0 48px rgba(232,121,249,0.55),
          0 0 0 1px rgba(255,255,255,0.05) inset;
        font-family: 'Orbitron', sans-serif;
        color: #fff;
        z-index: 4;
        animation: cs-streak-in 0.4s cubic-bezier(0.2,0.9,0.3,1.4) both, cs-streak-out 4s linear forwards;
      }
      .cs-streak-flames {
        position: absolute;
        inset: 0;
        overflow: hidden;
        pointer-events: none;
        border-radius: inherit;
      }
      .cs-streak-flame {
        position: absolute;
        bottom: -10px;
        width: 12px;
        height: 18px;
        border-radius: 6px;
        background: radial-gradient(circle at 50% 100%, ${PALETTE.gold}, rgba(245,158,11,0));
        opacity: 0;
        animation: cs-flame 1.4s linear infinite;
      }
      .cs-streak-flame:nth-child(odd)  { left: 10%; }
      .cs-streak-flame:nth-child(2n)   { left: 28%; }
      .cs-streak-flame:nth-child(3n)   { left: 46%; }
      .cs-streak-flame:nth-child(4n)   { left: 64%; }
      .cs-streak-flame:nth-child(5n+1) { left: 82%; }
      .cs-streak-flame:nth-child(6n+2) { left: 92%; }
      @keyframes cs-flame {
        0%   { transform: translateY(0) scale(0.7); opacity: 0; }
        20%  { opacity: 0.9; }
        100% { transform: translateY(-44px) scale(1.4); opacity: 0; }
      }
      .cs-streak-text {
        display: flex;
        align-items: center;
        gap: 18px;
        position: relative;
        z-index: 1;
      }
      .cs-streak-fire { font-size: 40px; line-height: 1; }
      .cs-streak-name-block { display: flex; flex-direction: column; }
      .cs-streak-eyebrow {
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 3px;
        color: rgba(255,255,255,0.7);
      }
      .cs-streak-name {
        font-size: 24px;
        font-weight: 900;
        letter-spacing: 1px;
        color: #fff;
        text-shadow: 0 0 14px ${PALETTE.magenta};
      }
      .cs-streak-mult {
        font-size: 36px;
        font-weight: 900;
        color: ${PALETTE.gold};
        text-shadow: 0 0 16px ${PALETTE.gold};
        padding: 4px 16px;
        background: rgba(0,0,0,0.45);
        border-radius: 999px;
      }
      @keyframes cs-streak-in {
        from { transform: translateX(110%); opacity: 0; }
        to   { transform: translateX(0); opacity: 1; }
      }
      @keyframes cs-streak-out {
        0%, 75% { opacity: 1; }
        100%    { opacity: 0; transform: translateX(40%); }
      }

      /* ── Found feed ───────────────────────────────────────────────── */
      .cs-feed {
        position: absolute;
        z-index: 2;
        display: flex;
        gap: 12px;
        pointer-events: none;
      }
      .cs-feed-bottom {
        bottom: 36px;
        left: 56px;
        right: 56px;
        flex-direction: row;
        flex-wrap: wrap;
      }
      .cs-feed-right {
        top: 240px;
        right: 36px;
        flex-direction: column;
        width: 360px;
      }
      .cs-feed-empty {
        font-family: 'Rajdhani', sans-serif;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: rgba(255,255,255,0.35);
        text-align: center;
        justify-content: center;
        padding: 16px;
      }
      .cs-feed-row {
        display: inline-flex;
        align-items: center;
        gap: 14px;
        padding: 12px 22px;
        background: rgba(8,5,18,0.75);
        border: 1px solid rgba(255,255,255,0.07);
        border-radius: 14px;
        backdrop-filter: blur(10px);
        font-family: 'Rajdhani', sans-serif;
        animation: cs-feed-slide 0.4s cubic-bezier(0.18,0.9,0.3,1.2) both;
      }
      @keyframes cs-feed-slide {
        from { transform: translateX(-16px); opacity: 0; }
        to   { transform: translateX(0); opacity: 1; }
      }
      .cs-feed-check {
        color: var(--cs-accent2);
        font-weight: 900;
        font-size: 18px;
        text-shadow: 0 0 10px var(--cs-accent2);
      }
      .cs-feed-word {
        font-family: 'Orbitron', monospace;
        font-weight: 800;
        letter-spacing: 2px;
        font-size: 20px;
      }
      .cs-feed-by {
        font-size: 14px;
        color: rgba(255,255,255,0.55);
      }
      .cs-feed-gems {
        font-family: 'Orbitron', monospace;
        font-weight: 800;
        font-size: 18px;
        color: var(--cs-accent2);
        text-shadow: 0 0 10px var(--cs-accent2);
      }
      .cs-feed-thematic .cs-feed-word { color: var(--cs-accent2); }
      .cs-feed-thematic .cs-feed-gems { color: var(--cs-magenta); text-shadow: 0 0 12px var(--cs-magenta); }
      .cs-feed-boss .cs-feed-word { color: ${PALETTE.gold}; text-shadow: 0 0 14px ${PALETTE.gold}; }
      .cs-feed-boss .cs-feed-gems { color: ${PALETTE.gold}; text-shadow: 0 0 14px ${PALETTE.gold}; }

      /* ── Boss winner takeover (full screen) ──────────────────────── */
      .cs-boss-winner {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        background: radial-gradient(circle at 50% 50%, rgba(245,158,11,0.28), rgba(0,0,0,0.92) 70%);
        backdrop-filter: blur(10px);
        animation: cs-boss-winner-in 0.6s cubic-bezier(0.2,0.9,0.3,1.4) both;
        z-index: 10;
      }
      @keyframes cs-boss-winner-in {
        from { opacity: 0; transform: scale(1.08); }
        to   { opacity: 1; transform: scale(1); }
      }
      .cs-boss-winner-text {
        text-align: center;
        font-family: 'Orbitron', sans-serif;
        position: relative;
        z-index: 2;
      }
      .cs-boss-winner-label {
        font-size: 24px;
        font-weight: 800;
        letter-spacing: 12px;
        color: ${PALETTE.gold};
        text-shadow: 0 0 20px ${PALETTE.gold};
      }
      .cs-boss-winner-word {
        font-size: 144px;
        font-weight: 900;
        letter-spacing: 8px;
        color: #fff;
        text-shadow:
          0 0 36px ${PALETTE.gold},
          0 0 72px ${PALETTE.red};
        margin-top: 16px;
        line-height: 1;
      }
      .cs-boss-winner-by {
        margin-top: 24px;
        font-size: 40px;
        font-weight: 800;
        color: ${PALETTE.magenta};
        text-shadow: 0 0 18px ${PALETTE.magenta};
      }
      .cs-boss-winner-prize {
        margin-top: 16px;
        font-size: 56px;
        font-weight: 900;
        color: ${PALETTE.gold};
        text-shadow: 0 0 24px ${PALETTE.gold};
      }
      .cs-boss-winner-confetti {
        position: absolute;
        inset: 0;
        overflow: hidden;
        pointer-events: none;
      }
      .cs-confetti-piece {
        position: absolute;
        top: -20px;
        width: 12px;
        height: 22px;
        animation: cs-confetti 2.2s cubic-bezier(0.18,0.6,0.3,1) both;
      }
      @keyframes cs-confetti {
        0%   { transform: translateY(0) rotate(0); opacity: 1; }
        100% { transform: translateY(1200px) rotate(720deg); opacity: 0; }
      }

      /* ── Idle state ──────────────────────────────────────────────── */
      .cs-stage.cs-idle {
        display: grid;
        place-items: center;
      }
      .cs-idle-block {
        text-align: center;
      }
      .cs-idle-eyebrow {
        font-family: 'Orbitron', sans-serif;
        font-size: 16px;
        font-weight: 800;
        letter-spacing: 8px;
        color: ${PALETTE.purple};
        text-shadow: 0 0 18px ${PALETTE.purple};
      }
      .cs-idle-title {
        font-family: 'Orbitron', sans-serif;
        font-size: 64px;
        font-weight: 900;
        letter-spacing: 12px;
        color: rgba(255,255,255,0.85);
        text-shadow: 0 0 30px rgba(147,51,234,0.6);
        margin-top: 16px;
      }
      .cs-idle-dot {
        margin: 32px auto 0;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: ${PALETTE.purple};
        box-shadow: 0 0 16px ${PALETTE.purple};
        animation: cs-pulse-dot 1.6s ease-in-out infinite;
      }
      @keyframes cs-pulse-dot {
        0%, 100% { transform: scale(1); opacity: 1; }
        50%      { transform: scale(1.6); opacity: 0.4; }
      }
    `}</style>
  );
}
